import type {
  BatchProcessDto,
  BatchProcessItemDto,
} from "@fp/admin-api-client";
import { BatchProcessItemStatus } from "@fp/admin-api-client";

export const STATUS_DWELL_MS = 1000;

const PACE_FLOOR = 0.4;
const PACE_SPREAD = 1.2;
const STEP_FLOOR = 0.85;
const STEP_SPREAD = 0.3;

export function getStatusDwellMs(
  itemId: string,
  status: BatchProcessItemStatus,
  dwellMs: number = STATUS_DWELL_MS,
): number {
  const pace = PACE_FLOOR + PACE_SPREAD * toRatio(itemId);
  const step = STEP_FLOOR + STEP_SPREAD * toRatio(`${itemId}:${status}`);

  return dwellMs * pace * step;
}

function toRatio(seed: string): number {
  let hash = 2166136261;

  for (let index = 0; index < seed.length; index++) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return ((hash >>> 0) % 1000) / 1000;
}

const countKeyByStatus = {
  [BatchProcessItemStatus.Pending]: "pendingCount",
  [BatchProcessItemStatus.InProcess]: "inProcessCount",
  [BatchProcessItemStatus.Deferred]: "deferredCount",
  [BatchProcessItemStatus.Succeeded]: "succeededCount",
  [BatchProcessItemStatus.NoAction]: "noActionCount",
  [BatchProcessItemStatus.Failed]: "failedCount",
} as const satisfies Record<BatchProcessItemStatus, keyof BatchProcessDto>;

export interface PacedItemStatus {
  displayed: BatchProcessItemStatus;
  queue: BatchProcessItemStatus[];
  displayedAt: number;
}

export type PacedItemStatuses = Readonly<Record<string, PacedItemStatus>>;

export function observeItemStatus(
  statuses: PacedItemStatuses,
  itemId: string,
  status: BatchProcessItemStatus,
  now: number,
): PacedItemStatuses {
  const paced = statuses[itemId];

  if (!paced) {
    return {
      ...statuses,
      [itemId]: { displayed: status, queue: [], displayedAt: now },
    };
  }

  const latest = paced.queue.at(-1) ?? paced.displayed;

  if (latest === status) {
    return statuses;
  }

  return {
    ...statuses,
    [itemId]: { ...paced, queue: [...paced.queue, status] },
  };
}

export function advanceItemStatuses(
  statuses: PacedItemStatuses,
  now: number,
  dwellMs: number = STATUS_DWELL_MS,
): PacedItemStatuses {
  const advanced: Record<string, PacedItemStatus> = { ...statuses };
  let hasAdvanced = false;

  for (const [itemId, paced] of Object.entries(statuses)) {
    const [next, ...rest] = paced.queue;

    if (
      next === undefined ||
      now - paced.displayedAt < getStatusDwellMs(itemId, paced.displayed, dwellMs)
    ) {
      continue;
    }

    advanced[itemId] = { displayed: next, queue: rest, displayedAt: now };
    hasAdvanced = true;
  }

  return hasAdvanced ? advanced : statuses;
}

export function getDisplayedItemStatus(
  statuses: PacedItemStatuses,
  itemId: string,
  fallback: BatchProcessItemStatus,
): BatchProcessItemStatus {
  return statuses[itemId]?.displayed ?? fallback;
}

export function paceBatchSummary(
  batch: BatchProcessDto,
  items: BatchProcessItemDto[],
  statuses: PacedItemStatuses,
): BatchProcessDto {
  if (items.length !== batch.totalCount) {
    return batch;
  }

  const counts = {
    pendingCount: 0,
    inProcessCount: 0,
    deferredCount: 0,
    succeededCount: 0,
    noActionCount: 0,
    failedCount: 0,
  };

  let isLagging = false;

  for (const item of items) {
    const displayed = getDisplayedItemStatus(statuses, item.id, item.status);

    counts[countKeyByStatus[displayed]] += 1;

    if (displayed !== item.status) {
      isLagging = true;
    }
  }

  if (!isLagging) {
    return batch;
  }

  return {
    ...batch,
    ...counts,
    isComplete:
      batch.isComplete &&
      counts.pendingCount === 0 &&
      counts.inProcessCount === 0 &&
      counts.deferredCount === 0,
  };
}